import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { InquiryForm } from "@/components/InquiryForm";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_florentina-lead/artifacts/3i1b3xsh_FS%20Logo%20Solid%20Mint.png";

const TERRAE_BOARD_URL =
  "https://customer-assets.emergentagent.com/job_florentina-lead/artifacts/mrwuw0kb_ChatGPT%20Image%20May%205%2C%202026%20at%2008_54_35%20AM.png";

const Landing = () => {
  return (
    <main data-testid="landing-page" style={{ background: "var(--fs-clay)" }}>
      {/* ============ HERO ============ */}
      <section
        data-testid="hero-section"
        className="w-full"
        style={{ background: "var(--fs-clay)" }}
      >
        <div className="max-w-[1280px] mx-auto px-6 sm:px-10 lg:px-16 pt-16 sm:pt-20 lg:pt-24 pb-24 sm:pb-28 lg:pb-32">
          {/* Logo top-left — the opening visual moment */}
          <Reveal>
            <div className="flex -ml-3 sm:-ml-4 lg:-ml-6">
              <img
                data-testid="hero-logo"
                src={LOGO_URL}
                alt="Florentina Studio"
                className="block h-auto w-[280px] sm:w-[380px] lg:w-[460px] select-none"
                draggable={false}
              />
            </div>
          </Reveal>

          {/* Headline + subline + CTA */}
          <div className="mt-8 sm:mt-10 lg:mt-12 max-w-[960px]">
            <Reveal delay={120}>
              <h1
                data-testid="hero-headline"
                className="font-serif-display leading-[1.02] tracking-[-0.015em] text-[44px] sm:text-[64px] lg:text-[88px]"
                style={{ color: "var(--fs-slate)" }}
              >
                Concepts, made visible.
              </h1>
            </Reveal>

            <Reveal delay={260}>
              <p
                data-testid="hero-subline"
                className="mt-8 sm:mt-10 lg:mt-12 max-w-[640px] text-[15px] sm:text-[17px] leading-[1.7]"
                style={{ color: "var(--fs-slate)" }}
              >
                Florentina Studio creates structured visual narratives for
                founders, creators, and brands shaping ideas that need a clear,
                elevated language.
              </p>
            </Reveal>

            <Reveal delay={420}>
              <div className="mt-12 sm:mt-14 lg:mt-16">
                <a
                  data-testid="hero-cta"
                  href="#form"
                  className="fs-btn"
                >
                  Begin a conversation
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ BOARD + PROCESS (combined) ============ */}
      <section
        data-testid="process-section"
        className="w-full"
        style={{ background: "var(--fs-mint)" }}
      >
        <div className="max-w-[1280px] mx-auto px-6 sm:px-10 lg:px-16 lg:py-0 py-20 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-stretch">
            {/* Concept board (left) — bleeds top and bottom of the section on lg */}
            <Reveal className="lg:col-span-7 xl:col-span-7 lg:self-stretch">
              <figure data-testid="terrae-board" className="m-0 h-full">
                <img
                  src={TERRAE_BOARD_URL}
                  alt="Terrae — culinary concept board, a sample of Florentina Studio's work"
                  className="block w-full h-auto lg:h-full lg:w-full lg:object-cover select-none"
                  draggable={false}
                />
                <figcaption
                  className="mt-5 lg:hidden label-caps"
                  style={{ color: "var(--fs-slate)" }}
                >
                  Terrae &nbsp;·&nbsp; Concept Board
                </figcaption>
              </figure>
            </Reveal>

            {/* Process steps (right) — vertically centered against the board */}
            <div className="lg:col-span-5 xl:col-span-5 lg:py-24 xl:py-28 lg:self-center lg:pl-2 xl:pl-4">
              <Reveal>
                <div
                  data-testid="process-label"
                  className="label-caps"
                  style={{ color: "var(--fs-slate)" }}
                >
                  The process
                </div>
              </Reveal>

              <Reveal delay={120}>
                <p
                  data-testid="process-intro"
                  className="font-serif-display mt-5 sm:mt-6 max-w-[480px] text-[17px] sm:text-[20px] md:text-[24px] lg:text-[32px] xl:text-[34px] leading-[1.32]"
                  style={{ color: "var(--fs-slate)" }}
                >
                  How a project moves from concept to delivery.
                </p>
              </Reveal>

              <div className="mt-10 sm:mt-12 flex flex-col gap-10 sm:gap-12">
                {[
                  {
                    n: "01",
                    title: "Concept board",
                    body: "A structured visual narrative.",
                  },
                  {
                    n: "02",
                    title: "Image development",
                    body: "Refinement, variations, expansion.",
                  },
                  {
                    n: "03",
                    title: "Visual direction",
                    body: "Assets you can use across your brand.",
                  },
                ].map((p, i) => (
                  <Reveal key={p.n} delay={i * 120}>
                    <div
                      data-testid={`process-step-${p.n}`}
                      className="flex items-baseline gap-6 sm:gap-8"
                    >
                      <div
                        className="font-serif-display leading-none text-[52px] sm:text-[60px] lg:text-[64px] shrink-0"
                        style={{ color: "var(--fs-brick)", fontWeight: 400 }}
                      >
                        {p.n}
                      </div>
                      <div className="pt-2 sm:pt-3">
                        <div
                          className="label-caps"
                          style={{ color: "var(--fs-slate)" }}
                        >
                          {p.title}
                        </div>
                        <p
                          className="mt-3 text-[15px] sm:text-[16px] leading-[1.65] max-w-[320px]"
                          style={{ color: "var(--fs-slate)" }}
                        >
                          {p.body}
                        </p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FORM ============ */}
      <section
        id="form"
        data-testid="form-section"
        className="w-full scroll-mt-12"
        style={{ background: "var(--fs-clay)" }}
      >
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-16 py-20 sm:py-24 lg:py-28">
          <Reveal>
            <div
              data-testid="form-label"
              className="label-caps"
              style={{ color: "var(--fs-slate)" }}
            >
              Begin a conversation
            </div>
          </Reveal>

          <Reveal delay={140}>
            <h2
              data-testid="form-headline"
              className="font-serif-display mt-6 sm:mt-7 max-w-[968px] leading-[1.32] text-[17px] sm:text-[20px] md:text-[24px] lg:text-[32px] xl:text-[34px]"
              style={{ color: "var(--fs-slate)" }}
            >
              <span className="block">
                Tell me what you are building. I will sit with your idea.
              </span>
              <span className="block">
                Then we will meet for thirty minutes, and together we will give
                it form.
              </span>
              <span className="block">Choose your time below.</span>
            </h2>
          </Reveal>

          <Reveal delay={300}>
            <div className="mt-10 sm:mt-14">
              <InquiryForm />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer
        data-testid="footer"
        className="w-full"
        style={{ background: "var(--fs-clay)" }}
      >
        <div className="max-w-[1280px] mx-auto px-6 sm:px-10 lg:px-16 pt-8 pb-20 sm:pb-24 flex flex-col items-center">
          <img
            data-testid="footer-logo"
            src={LOGO_URL}
            alt="Florentina Studio"
            className="block h-auto w-[220px] sm:w-[260px] lg:w-[300px] select-none"
            draggable={false}
          />
          <div
            className="mt-6 label-caps text-center"
            style={{ color: "var(--fs-slate)" }}
          >
            Florentina Studio &nbsp;·&nbsp; Creative Direction &nbsp;·&nbsp; 2026
          </div>
        </div>
      </footer>
    </main>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
